import { Zap, CheckCircle2, Sparkles } from 'lucide-react';

/**
 * Copilot-branded fit-score primitives — shared across Discover, Events and
 * Mentors so the UX reads the same everywhere.
 *
 * Colour language: the score band drives the hue so a founder can spot
 * high vs low fit instantly on a listing rail.
 *   ≥ 80  Excellent → emerald  (green, high-confidence pull)
 *   ≥ 65  Strong    → teal     (still a green family, slightly cooler)
 *   ≥ 45  Moderate  → amber    (warm, worth a look)
 *   < 45  Light     → rose     (soft red, "match is thin — skip if pressed for time")
 *
 * Dark mode: every colour has a `dark:` counterpart tuned for readability
 * on `bg-neutral-950/900`. We shift text tones two steps LIGHTER
 * (e.g. `text-emerald-800` → `dark:text-emerald-200`) and swap the
 * pale-tinted fills for translucent overlays (`dark:bg-emerald-900/40`)
 * so the pill still reads coloured but doesn't glare against dark chrome.
 */

const BAND_PALETTE = {
  excellent: {
    label: 'Excellent Fit',
    // list-card pill (light AND dark)
    pillBorder: 'border-emerald-400/70 dark:border-emerald-400/50',
    pillBg: 'bg-gradient-to-r from-emerald-50 via-emerald-50 to-green-50 dark:from-emerald-900/40 dark:via-emerald-900/40 dark:to-green-900/40',
    pillText: 'text-emerald-800 dark:text-emerald-200',
    pillIcon: 'fill-emerald-500 text-emerald-500 dark:fill-emerald-300 dark:text-emerald-300',
    // detail card
    cardBorder: 'border-emerald-300 dark:border-emerald-500/40',
    cardBg: 'bg-gradient-to-br from-emerald-50 via-green-50/60 to-white dark:from-emerald-900/30 dark:via-emerald-900/20 dark:to-transparent',
    cardShadow: 'shadow-[inset_0_1px_0_rgba(16,185,129,0.18)] dark:shadow-[inset_0_1px_0_rgba(16,185,129,0.35)]',
    scoreGradient: 'from-emerald-600 via-green-500 to-emerald-500 dark:from-emerald-300 dark:via-green-300 dark:to-emerald-200',
    cardText: 'text-emerald-900 dark:text-emerald-100',
    cardMutedText: 'text-emerald-800/70 dark:text-emerald-200/70',
    reasonIcon: 'text-emerald-600 dark:text-emerald-300',
    pillCopilot: 'from-emerald-500 to-green-500',
    zap: 'fill-emerald-500 text-emerald-500 dark:fill-emerald-300 dark:text-emerald-300',
  },
  strong: {
    label: 'Strong Fit',
    pillBorder: 'border-teal-400/70 dark:border-teal-400/50',
    pillBg: 'bg-gradient-to-r from-teal-50 to-cyan-50 dark:from-teal-900/40 dark:to-cyan-900/40',
    pillText: 'text-teal-800 dark:text-teal-200',
    pillIcon: 'fill-teal-500 text-teal-500 dark:fill-teal-300 dark:text-teal-300',
    cardBorder: 'border-teal-300 dark:border-teal-500/40',
    cardBg: 'bg-gradient-to-br from-teal-50 via-cyan-50/60 to-white dark:from-teal-900/30 dark:via-cyan-900/20 dark:to-transparent',
    cardShadow: 'shadow-[inset_0_1px_0_rgba(20,184,166,0.18)] dark:shadow-[inset_0_1px_0_rgba(20,184,166,0.35)]',
    scoreGradient: 'from-teal-600 via-cyan-500 to-teal-500 dark:from-teal-300 dark:via-cyan-300 dark:to-teal-200',
    cardText: 'text-teal-900 dark:text-teal-100',
    cardMutedText: 'text-teal-800/70 dark:text-teal-200/70',
    reasonIcon: 'text-teal-600 dark:text-teal-300',
    pillCopilot: 'from-teal-500 to-cyan-500',
    zap: 'fill-teal-500 text-teal-500 dark:fill-teal-300 dark:text-teal-300',
  },
  moderate: {
    label: 'Moderate Fit',
    pillBorder: 'border-amber-400/70 dark:border-amber-400/50',
    pillBg: 'bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/40 dark:to-orange-900/40',
    pillText: 'text-amber-800 dark:text-amber-200',
    pillIcon: 'fill-amber-500 text-amber-500 dark:fill-amber-300 dark:text-amber-300',
    cardBorder: 'border-amber-300 dark:border-amber-500/40',
    cardBg: 'bg-gradient-to-br from-amber-50 via-orange-50/60 to-white dark:from-amber-900/30 dark:via-orange-900/20 dark:to-transparent',
    cardShadow: 'shadow-[inset_0_1px_0_rgba(245,158,11,0.18)] dark:shadow-[inset_0_1px_0_rgba(245,158,11,0.35)]',
    scoreGradient: 'from-orange-600 via-amber-500 to-orange-500 dark:from-orange-300 dark:via-amber-300 dark:to-orange-200',
    cardText: 'text-amber-900 dark:text-amber-100',
    cardMutedText: 'text-amber-800/70 dark:text-amber-200/70',
    reasonIcon: 'text-orange-600 dark:text-orange-300',
    pillCopilot: 'from-orange-500 to-amber-500',
    zap: 'fill-amber-500 text-amber-500 dark:fill-amber-300 dark:text-amber-300',
  },
  light: {
    label: 'Light Match',
    pillBorder: 'border-rose-300/70 dark:border-rose-400/50',
    pillBg: 'bg-rose-50/70 dark:bg-rose-900/30',
    pillText: 'text-rose-700 dark:text-rose-200',
    pillIcon: 'fill-rose-400 text-rose-400 dark:fill-rose-300 dark:text-rose-300',
    cardBorder: 'border-rose-300 dark:border-rose-500/40',
    cardBg: 'bg-gradient-to-br from-rose-50 via-orange-50/50 to-white dark:from-rose-900/25 dark:via-orange-900/15 dark:to-transparent',
    cardShadow: 'shadow-[inset_0_1px_0_rgba(244,63,94,0.14)] dark:shadow-[inset_0_1px_0_rgba(244,63,94,0.32)]',
    scoreGradient: 'from-rose-500 via-orange-400 to-rose-400 dark:from-rose-300 dark:via-orange-300 dark:to-rose-200',
    cardText: 'text-rose-900 dark:text-rose-100',
    cardMutedText: 'text-rose-800/70 dark:text-rose-200/70',
    reasonIcon: 'text-rose-500 dark:text-rose-300',
    pillCopilot: 'from-rose-500 to-orange-500',
    zap: 'fill-rose-400 text-rose-400 dark:fill-rose-300 dark:text-rose-300',
  },
};

export function bandFor(score) {
  if (score >= 80) return { key: 'excellent', ...BAND_PALETTE.excellent };
  if (score >= 65) return { key: 'strong', ...BAND_PALETTE.strong };
  if (score >= 45) return { key: 'moderate', ...BAND_PALETTE.moderate };
  return { key: 'light', ...BAND_PALETTE.light };
}

// ─── Compact list-card pill ────────────────────────────────────────────
// Just: [⚡ 46% match] — colour-coded by band. No reason chips (they live
// on the detail card). Reasons still land in the `title` attribute so
// desktop users hovering the pill get the full explanation.
export function FitBadge({ fit, className = '', testId = 'fit-badge' }) {
  if (!fit || typeof fit.score !== 'number') return null;
  const { score, reasons = [], is_baseline } = fit;
  const b = bandFor(score);
  const title = is_baseline
    ? 'Complete your venture profile so Copilot can score this precisely.'
    : (reasons.join(' · ') || `${b.label} · ${score}%`);

  return (
    <div
      className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-bold ${b.pillBorder} ${b.pillBg} ${b.pillText} ${className}`}
      data-testid={testId}
      title={title}
    >
      <Zap className={`h-3 w-3 ${b.pillIcon}`} />
      {score}% match
    </div>
  );
}

// ─── Big detail-page card ──────────────────────────────────────────────
export function FitScoreCard({ fit, title = 'Fit Score', className = '', testId = 'fit-score-card' }) {
  if (!fit || typeof fit.score !== 'number') return null;
  const { score, reasons = [], is_baseline } = fit;
  const b = bandFor(score);

  return (
    <div className={className} data-testid={testId}>
      <p className="text-[11px] font-grotesk uppercase tracking-[0.2em] text-muted-foreground mb-2">{title}</p>
      <div className={`rounded-2xl border ${b.cardBorder} ${b.cardBg} p-5 ${b.cardShadow}`}>
        <div className="flex items-start gap-5">
          <div className="text-center shrink-0">
            <div
              className={`font-display text-5xl leading-none bg-gradient-to-br ${b.scoreGradient} bg-clip-text text-transparent`}
              data-testid={`${testId}-value`}
            >
              {score}
            </div>
            <div className={`text-[10px] uppercase tracking-[0.18em] ${b.cardMutedText} mt-1.5 font-grotesk font-semibold`}>out of 100</div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className={`inline-flex items-center gap-1 text-[10px] font-grotesk font-bold uppercase tracking-widest rounded-full px-2 py-0.5 bg-gradient-to-r ${b.pillCopilot} text-white`}>
                <Sparkles className="h-3 w-3" /> Copilot
              </span>
              <p className={`text-base font-bold ${b.cardText} flex items-center gap-1.5`} data-testid={`${testId}-band`}>
                <Zap className={`h-4 w-4 ${b.zap}`} /> {b.label}
              </p>
            </div>
            {is_baseline ? (
              <p className={`text-xs ${b.cardMutedText}`}>Complete your venture profile so Copilot can score this precisely for you.</p>
            ) : (
              <ul className={`mt-2 space-y-1.5 text-sm ${b.cardText}`}>
                {reasons.slice(0, 6).map((r, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <CheckCircle2 className={`h-3.5 w-3.5 mt-0.5 shrink-0 ${b.reasonIcon}`} />
                    <span className="leading-snug">{r}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default FitBadge;
